import * as api from '@apis/discount-coupon';
import { DiscountCoupon } from '@models/discount-coupon';
import { useDiscountCouponsStore } from '@stores/discountCouponsStore';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

interface SelectDiscountCouponModalProps {
  open?: boolean;
  onAddCoupons: (coupons: DiscountCoupon[]) => void;
  onClose?: () => void;
  selectLimit?: number;
}

export const SelectDiscountCouponModal: React.FC<SelectDiscountCouponModalProps> = (props) => {
  const [selectedCoupons, setSelectedCoupons] = useState<DiscountCoupon[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const store = useDiscountCouponsStore();

  const discountCouponSearchQuery = useQuery({
    queryKey: [`admin-search-discount-coupons`, JSON.stringify({ ...store.searchQuery, status: 'active' })],
    queryFn: () =>
      api.searchDiscountCoupons({
        ...store.searchQuery,
        status: 'active',
      }),
  });

  useEffect(() => {
    const searchResult = discountCouponSearchQuery.data?.data;
    if (searchResult) {
      store.setDiscountCouponsContainer(searchResult);
    }
  }, [discountCouponSearchQuery.data?.data]);

  const TABLE_COLUMNS: ColumnsType<DiscountCoupon> = [
    {
      fixed: 'left',
      title: () => {
        return (
          <Checkbox
            disabled={!!props.selectLimit}
            checked={
              JSON.stringify(selectedCoupons.map((c) => c._id).sort()) ===
              JSON.stringify(
                (store.searchResultContainer.contents || [])
                  .filter((coupon) => !!coupon)
                  .map((coupon) => coupon._id)
                  .sort(),
              )
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedCoupons(store.searchResultContainer.contents);
              } else {
                setSelectedCoupons([]);
              }
            }}></Checkbox>
        );
      },
      width: 60,
      render: (record) => {
        return (
          <Checkbox
            disabled={
              !!props.selectLimit &&
              selectedCoupons.length === props.selectLimit &&
              selectedCoupons[0]?._id !== record?._id
            }
            onChange={(e) => {
              setSelectedCoupons((values) => {
                const checked = e.target.checked;
                const newValues = [...values];
                if (checked) {
                  if (!newValues.find((item) => item._id === record._id)) {
                    newValues.push(record);
                  }
                  return newValues;
                } else {
                  return newValues.filter((value) => value._id !== record._id);
                }
              });
            }}
            checked={!!selectedCoupons.find((item) => item?._id === record?._id)}></Checkbox>
        );
      },
    },
    {
      title: '쿠폰명',
      width: 200,
      dataIndex: 'name',
      render: (name) => `${name}`,
    },
    {
      title: '설명',
      width: 300,
      dataIndex: 'description',
      render: (description) => `${description || '-'}`,
    },
    {
      title: '할인 타입',
      width: 120,
      dataIndex: 'discountType',
      render: (discountType) => {
        if (discountType === 'percentage') return '퍼센트 할인';
        if (discountType === 'amount') return '금액 할인';
        if (discountType === 'unit') return '구매수량 당 할인';
        return discountType;
      },
    },
    {
      title: '할인율/금액',
      width: 120,
      render: (record) => {
        if (record.discountType === 'percentage') {
          return `${record.discountPercent}%`;
        } else if (record.discountType === 'amount') {
          return `${record.discountAmount?.toLocaleString()}원`;
        }
        return '-';
      },
    },
    {
      title: '최대 할인 금액',
      width: 120,
      dataIndex: 'maxDiscountAmount',
      render: (maxDiscountAmount) => {
        return maxDiscountAmount ? `${maxDiscountAmount?.toLocaleString()}원` : '-';
      },
    },
    {
      title: '최소 주문 금액',
      width: 120,
      dataIndex: 'minPurchaseAmount',
      render: (minPurchaseAmount) => `${minPurchaseAmount?.toLocaleString()}원`,
    },
    {
      title: '발급 방식',
      width: 100,
      dataIndex: 'issuanceType',
      render: (issuanceType) => {
        if (issuanceType === 'download') return '다운로드';
        if (issuanceType === 'direct') return '직접 발급';
        return issuanceType;
      },
    },
    {
      title: '상태',
      width: 80,
      dataIndex: 'status',
      render: (status) => {
        if (status === 'active') return '활성';
        if (status === 'inactive') return '비활성';
        if (status === 'expired') return '만료';
        return status;
      },
    },
    {
      title: '발급 기간',
      width: 200,
      render: (record) => {
        return (
          <div>
            <div>{moment(record.issueStartDate).format('YYYY.MM.DD')}</div>
            <div style={{ fontWeight: 'bold' }}>{moment(record.issueEndDate).format('YYYY.MM.DD')}</div>
          </div>
        );
      },
    },
    {
      title: '사용 기한',
      width: 200,
      render: (record) => {
        return (
          <div>
            <div>{moment(record.validFrom).format('YYYY.MM.DD')}</div>
            <div style={{ fontWeight: 'bold' }}>{moment(record.validUntil).format('YYYY.MM.DD')}</div>
          </div>
        );
      },
    },
    {
      title: '선택',
      dataIndex: '_id',
      render: (_id, record) => {
        return (
          <Button
            key={_id}
            onClick={() => {
              props.onAddCoupons && props.onAddCoupons([record]);
            }}>
            선택
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      open={props.open}
      footer={false}
      onCancel={props.onClose}
      onOk={props.onClose}
      width={1400}
      zIndex={1500}
      styles={{ body: { height: '600px', overflow: 'auto' } }}>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            style={{ width: '240px' }}
            value={searchKeyword}
            placeholder={'검색어 입력 후 엔터'}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
            }}
            onKeyUp={(e) => {
              if (e.code === 'Enter' && searchKeyword) {
                store.updateSearchQuery({ keyword: searchKeyword });
                setSelectedCoupons([]);
              }
            }}></Input>
          <Button
            onClick={() => {
              setSearchKeyword(undefined);
              store.resetSearchQuery();
              setSelectedCoupons([]);
            }}>
            검색 초기화
          </Button>
        </div>

        <div style={{ marginRight: '20px' }}>
          {(selectedCoupons.filter((item) => item._id) || []).length > 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div>{`${(selectedCoupons.filter((item) => item._id) || []).length}개 선택 항목을`} </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Button
                  onClick={() => {
                    props.onAddCoupons && props.onAddCoupons(selectedCoupons.filter((item) => item._id));
                    setSelectedCoupons([]);
                    props.onClose && props.onClose();
                  }}>
                  추가하기
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Table
        scroll={{ x: 'max-content', y: 'calc(100dvh - 500px)' }}
        columns={TABLE_COLUMNS}
        rowKey={(record) => record?._id}
        dataSource={store.searchResultContainer.contents.filter((coupon) => coupon?._id)}
        pagination={{
          current: (store.searchResultContainer.page || 0) + 1,
          pageSize: store.searchResultContainer.size || 20,
          total: store.searchResultContainer.total,
        }}
        loading={discountCouponSearchQuery.isLoading}
        onChange={(e) => {
          store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
        }}
      />
    </Modal>
  );
};

export default SelectDiscountCouponModal;
